import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import client from "../../../api/client";
import { getUserCountry, getFormattedPrice } from "../../../utils/currency";
import { getCache, setCache } from "../../../utils/cache";

const ArdrendMainCourses = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);
  const [rawCourses, setRawCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const country = await getUserCountry();
        let coursesList = getCache('courses_data');
        if (!coursesList) {
          const res = await client.get('/v2/courses');
          coursesList = Array.isArray(res.data) ? res.data : (res.data.data || []);
          setCache('courses_data', coursesList);
        }
        const mapped = [...coursesList].reverse().slice(0, 4).map(course => ({
          id: course.id,
          title: course.title,
          desc: (course.description || '').replace(/<[^>]*>?/gm, '').substring(0, 160) + '...',
          img: course.image_url || "https://images.unsplash.com/photo-1550439062-609e1531270e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
          cost: getFormattedPrice(course, country),
          duration: course.duration || "12 Weeks",
          link: `/online-course/${course.slug}`,
          isLive: Number(course.course_type_id) === 1,
        }));
        setRawCourses(mapped);
        if (mapped.length > 0) setActiveId(mapped[0].id);
      } catch (err) {
        console.error("Error fetching courses", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Derived separately from the fetch so switching language re-translates the
  // tags instantly without re-hitting the API/cache.
  const courses = useMemo(
    () => rawCourses.map((course) => ({
      ...course,
      tags: [
        course.isLive ? t("home.mainCourses.liveCohort") : t("home.mainCourses.selfPaced"),
        course.duration,
        t("home.mainCourses.certificateIncluded"),
      ],
    })),
    [rawCourses, t]
  );

  return (
    <section className="py-24 bg-[#0b0f19]">
      <div className="max-w-[1560px] mx-auto px-6">
        <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-primary mb-6 flex items-center gap-3">
          <span className="block w-8 h-[2px] bg-primary"></span>
          {t("home.mainCourses.kicker")}
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-8">
          <div className="w-full lg:w-1/2">
            <h2 className="text-white text-3xl md:text-5xl font-bold">
              {t("home.mainCourses.title")}<span className="text-primary">.</span>
            </h2>
          </div>
          <div className="w-full lg:w-1/2 flex flex-col md:flex-row lg:justify-end items-start md:items-end gap-6">
            <p className="text-gray-400 text-[16px] max-w-[400px] leading-relaxed border-l border-primary/30 pl-6 hidden md:block">
              {t("home.mainCourses.subtitle")}
            </p>
            <Link to="/courses" className="h-12 px-8 bg-white hover:bg-primary text-[#0b0f19] hover:text-white rounded-full flex items-center gap-2 font-bold transition-colors shrink-0">
              {t("home.mainCourses.viewAll")}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-4 h-[600px]">
            {[1,2,3,4].map(n => (
              <div key={n} className="flex-1 rounded-3xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:h-[600px] gap-4">
            {courses.map((course) => {
              const isActive = activeId === course.id;
              return (
                <div
                  key={course.id}
                  onMouseEnter={() => setActiveId(course.id)}
                  onClick={() => navigate(course.link)}
                  className={`relative rounded-3xl overflow-hidden transition-all duration-700 ease-in-out cursor-pointer ${
                    isActive
                      ? 'h-[400px] lg:h-full lg:flex-[5_5_0%] shadow-2xl'
                      : 'h-[150px] lg:h-full lg:flex-[2_2_0%] opacity-70 grayscale hover:grayscale-0'
                  }`}
                >
                  <img
                    src={course.img}
                    alt={course.title}
                    className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ${isActive ? 'scale-100' : 'scale-110'}`}
                  />
                  <div className={`absolute inset-0 transition-colors duration-700 ${isActive ? 'bg-gradient-to-t from-[#0b0f19] via-[#0b0f19]/80 to-transparent' : 'bg-[#0b0f19]/80'}`}></div>

                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    <div className={`flex flex-wrap gap-3 transition-all duration-700 ${isActive ? 'mb-6 opacity-100' : 'mb-0 opacity-0 h-0 overflow-hidden'}`}>
                      {course.tags.map((tag, i) => (
                        <span key={i} className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] md:text-[11px] font-bold uppercase tracking-wider whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className={`text-white font-bold transition-all duration-700 ${isActive ? 'text-2xl md:text-3xl lg:text-4xl mb-6' : 'text-xl md:text-2xl lg:text-3xl mb-0'}`}>
                      {course.title}
                    </h3>
                    <div className={`overflow-hidden transition-all duration-700 ease-in-out ${isActive ? 'max-h-[250px] opacity-100 translate-y-0' : 'max-h-0 opacity-0 translate-y-4'}`}>
                      <p className="text-gray-300 text-[15px] mb-4 leading-relaxed hidden sm:block">
                        {course.desc}
                      </p>
                      <p className="text-primary font-bold text-lg mb-6">{course.cost}</p>
                      <button className="bg-primary hover:bg-[#b0451e] text-white transition-colors rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-4 group w-max shadow-md">
                        <span className="font-bold uppercase tracking-wider text-[12px] md:text-[13px]">{t("home.mainCourses.viewCurriculum")}</span>
                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-primary group-hover:bg-[#0b0f19] group-hover:text-white transition-colors shrink-0">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 transition-transform group-hover:translate-x-0.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                          </svg>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ArdrendMainCourses;
